import { useEffect } from "react";
import { PrintSettingsStateTypes } from "../types/print_settings";
import { getProductBarcode, Product } from "../types/product";
import { PrintLabelKind } from "../utils/printGrouping";
import {
  createPrintLabelLayout,
  PrintPriceDisplayOptions,
} from "../utils/printLabelLayout";
import RenderList, { CardItem } from "./RenderList";
import { usePrintLabelLayouts } from "./PrintLabelLayoutContext";

interface PrintLabelProps {
  settings: PrintSettingsStateTypes;
  data: Product;
  kind: PrintLabelKind;
  showPriceAdjustment?: boolean;
  showPriceStrikethrough?: boolean;
}

export const getPrintLabelLayoutKey = (data: Product) =>
  `${data.itemNumber || data.itemName}::${getProductBarcode(data)}`;

const PrintLabel: React.FC<PrintLabelProps> = ({
  settings,
  data,
  kind,
  showPriceAdjustment = false,
  showPriceStrikethrough = false,
}) => {
  const { layouts, syncLayout, updateLayout } = usePrintLabelLayouts();
  const layoutKey = getPrintLabelLayoutKey(data);
  const priceOptions: PrintPriceDisplayOptions = { showPriceAdjustment, showPriceStrikethrough };
  const fallbackLayout = createPrintLabelLayout(data, settings, priceOptions);
  const layout = layouts[layoutKey] || fallbackLayout;
  const items = kind === "display" ? layout.cards : layout.lists;

  useEffect(() => {
    syncLayout(layoutKey, data, settings, {
      showPriceAdjustment,
      showPriceStrikethrough,
    });
  }, [data, layoutKey, settings, showPriceAdjustment, showPriceStrikethrough, syncLayout]);

  const setItems = (update: React.SetStateAction<CardItem[]>) => {
    updateLayout(layoutKey, (current) => {
      const previousItems = kind === "display" ? current.cards : current.lists;
      const nextItems =
        typeof update === "function" ? update(previousItems) : update;

      return kind === "display"
        ? { ...current, cards: nextItems }
        : { ...current, lists: nextItems };
    });
  };

  return <RenderList data={items} setState={setItems} />;
};

export default PrintLabel;
